import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import type { AgeingLine, ArrearsAgeingReport, ParLine, ReportDimension } from '@/api/reports-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import type { ApiError } from '@/lib/problem-details'

import { ReportExportButtons } from './report-export-buttons'
import { DIMENSION_LABEL, REPORT_DIMENSIONS, formatRatio, resolveBranchLabel, today } from './constants'
import { BranchFilterField, LoanOfficerFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useArrearsAgeing, useReportsBranchReach, usePortfolioAtRisk } from './queries'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

type ParTab = 'buckets' | 'facilities'

const PANEL_ID = 'reports-par-panel'
const DEFAULT_PAGE_SIZE = 50

/** The endpoint takes no ordering parameter, so there is nothing for a sort click to ask for. */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

/**
 * Portfolio at risk and arrears ageing.
 *
 * Two views of the same arrears, on one screen because they answer the same question at two
 * grains: **by bucket** groups every facility into a dimension member and an ageing band and totals
 * each cell, the shape a branch manager reads first; **facilities in arrears** is the worklist
 * behind any one of those cells, worst first, the shape a collections officer actually works from.
 *
 * Read-only, so both tabs work at any width — the tables scroll sideways inside their own
 * container rather than pushing the page.
 */
export function ParSection() {
  const permissions = useReportsPermissions()

  const [tab, setTab] = useState<ParTab>('buckets')
  const [asOf, setAsOf] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [loanOfficerId, setLoanOfficerId] = useState<string | null>(null)
  const [dimension, setDimension] = useState<ReportDimension>('Branch')
  const [arrearsOnly, setArrearsOnly] = useState(true)
  const [minimumDaysInArrears, setMinimumDaysInArrears] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const parQuery = usePortfolioAtRisk(
    { asOf, dimension, branchId, loanOfficerId },
    permissions.canViewPar && tab === 'buckets' && asOf !== '',
  )

  const minDays = minimumDaysInArrears.trim() === '' ? null : Number(minimumDaysInArrears)
  const ageingQuery = useArrearsAgeing(
    { asOf, arrearsOnly, minimumDaysInArrears: minDays, branchId, loanOfficerId, page, pageSize },
    permissions.canViewPar && tab === 'facilities' && asOf !== '',
  )

  if (!permissions.canViewPar) {
    return (
      <RestrictedPanel
        what="Portfolio at risk and arrears ageing"
        permission={REPORTS_PERMISSIONS.parView}
      />
    )
  }

  const tabs: TabDefinition<ParTab>[] = [
    { id: 'buckets', label: 'By ageing bucket' },
    { id: 'facilities', label: 'Facilities in arrears' },
  ]

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid gap-3 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="par-as-of">As of</Label>
              <Input
                id="par-as-of"
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
              />
            </div>

            <BranchFilterField
              id="par-branch"
              branchId={branchId}
              onChange={(next) => {
                setBranchId(next)
                setLoanOfficerId(null)
              }}
            />

            <LoanOfficerFilterField
              id="par-officer"
              branchId={branchId}
              loanOfficerId={loanOfficerId}
              onChange={setLoanOfficerId}
            />

            {tab === 'buckets' ? (
              <div className="space-y-1.5">
                <Label htmlFor="par-dimension">Break down by</Label>
                <Select value={dimension} onValueChange={(next) => setDimension(next as ReportDimension)}>
                  <SelectTrigger id="par-dimension">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_DIMENSIONS.map((candidate) => (
                      <SelectItem key={candidate} value={candidate}>
                        {DIMENSION_LABEL[candidate]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {tab === 'facilities' ? (
              <div className="space-y-1.5">
                <Label htmlFor="par-min-days">Minimum days in arrears</Label>
                <Input
                  id="par-min-days"
                  type="number"
                  min={0}
                  inputMode="numeric"
                  placeholder="Any"
                  value={minimumDaysInArrears}
                  onChange={(event) => {
                    setMinimumDaysInArrears(event.target.value)
                    setPage(1)
                  }}
                />
              </div>
            ) : null}
          </div>

          {tab === 'facilities' ? (
            <label className="flex items-center gap-2 text-sm text-body max-lg:min-h-11">
              <Checkbox
                checked={arrearsOnly}
                onCheckedChange={(next) => {
                  setArrearsOnly(next === true)
                  setPage(1)
                }}
              />
              Arrears only — untick to see facilities that are current as well
            </label>
          ) : null}
        </CardContent>
      </Card>

      <TabStrip<ParTab>
        tabs={tabs}
        active={tab}
        onChange={setTab}
        panelId={PANEL_ID}
        label="Portfolio at risk sections"
      />

      <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`} className="space-y-4">
        {tab === 'buckets' ? (
          parQuery.isPending ? null : parQuery.isError ? null : parQuery.data ? (
            <>
              <ReportHeaderNotice header={parQuery.data.header} />
              <ReportExportButtons
                query={{ report: 'PortfolioAtRisk', asOf, dimension, branchId, loanOfficerId }}
              />
              <ParTotalCard total={parQuery.data.total} />
              <ParDimensionTable lines={parQuery.data.lines} />
              <ParBucketTable line={parQuery.data.total} />
            </>
          ) : null
        ) : (
          <AgeingTable
            report={ageingQuery.data}
            isLoading={ageingQuery.isPending}
            isFetching={ageingQuery.isFetching}
            error={ageingQuery.isError ? ageingQuery.error : null}
            onRetry={() => void ageingQuery.refetch()}
            page={page}
            pageSize={pageSize}
            onPaginationChange={onPaginationChange}
            branchId={branchId}
            loanOfficerId={loanOfficerId}
            asOf={asOf}
            arrearsOnly={arrearsOnly}
            minimumDaysInArrears={minDays}
          />
        )}
      </div>
    </div>
  )
}

function ParTotalCard({ total }: { total: ParLine }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{DIMENSION_LABEL[total.dimension]} total</CardTitle>
        <Badge tone="neutral">{total.facilityCount} facilities</Badge>
      </CardHeader>
      <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Gross portfolio" money={total.grossPortfolio} />
        <Stat label="Total outstanding" money={total.totalOutstanding} />
        <Stat label="Portfolio at risk" money={total.portfolioAtRisk} />
        <Stat label="PAR ratio" value={formatRatio(total.parRatio)} />
        <Stat label="Amount in arrears" money={total.amountInArrears} />
        <Stat label="Provision required" money={total.provisionRequired} />
        <Stat label="Unclassified facilities" value={String(total.unclassifiedFacilityCount)} />
      </CardContent>
    </Card>
  )
}

function Stat({
  label,
  money,
  value,
}: {
  label: string
  money?: { minor: number; currency: string }
  value?: string
}) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      {money ? (
        <MoneyDisplay value={money.minor} currency={money.currency} emphasis="strong" className="text-left" />
      ) : (
        <p className="numeric text-sm font-semibold tabular-nums text-heading">{value}</p>
      )}
    </div>
  )
}

function ParDimensionTable({ lines }: { lines: ParLine[] }) {
  const reach = useReportsBranchReach()

  return (
    <Card>
      <CardHeader>
        <CardTitle>By {lines[0] ? DIMENSION_LABEL[lines[0].dimension].toLowerCase() : 'dimension'}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {lines.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-body">Nothing in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead scope="col">Member</TableHead>
                  <TableHead scope="col" className="text-right">
                    Facilities
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Outstanding
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    At risk
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    PAR ratio
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Provision required
                  </TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.dimensionKey}>
                    <TableCell className="text-sm font-medium text-heading">
                      {line.dimension === 'Branch'
                        ? resolveBranchLabel(reach.branches, line.dimensionKey)
                        : line.dimensionLabel}
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">{line.facilityCount}</TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.totalOutstanding.minor} currency={line.totalOutstanding.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.portfolioAtRisk.minor} currency={line.portfolioAtRisk.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">{formatRatio(line.parRatio)}</TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.provisionRequired.minor} currency={line.provisionRequired.currency} symbol={false} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ParBucketTable({ line }: { line: ParLine }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ageing buckets — {DIMENSION_LABEL[line.dimension].toLowerCase()}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {line.buckets.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-body">
            No classification scheme was in force for this date, so nothing could be aged.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead scope="col">Class</TableHead>
                  <TableHead scope="col">Range</TableHead>
                  <TableHead scope="col" className="text-right">
                    Facilities
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Outstanding
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    In arrears
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Share
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Cumulative PAR
                  </TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {line.buckets.map((bucket) => (
                  <TableRow key={bucket.classCode}>
                    <TableCell className="text-sm">
                      {bucket.className}
                      {bucket.isAtRisk ? (
                        <Badge tone="danger" className="ml-1.5">
                          At risk
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-xs text-muted">{bucket.range}</TableCell>
                    <TableCell className="numeric text-right tabular-nums">{bucket.facilityCount}</TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={bucket.totalOutstanding.minor} currency={bucket.totalOutstanding.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={bucket.amountInArrears.minor} currency={bucket.amountInArrears.currency} symbol={false} />
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">{formatRatio(bucket.shareOfPortfolio)}</TableCell>
                    <TableCell className="numeric text-right tabular-nums">{formatRatio(bucket.cumulativeParRatio)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const ageingHelper = dataTableColumns<AgeingLine>()

const ageingColumns: DataTableColumn<AgeingLine>[] = ageingHelper.columns([
  ageingHelper.accessor('loanAccountNumber', {
    header: 'Loan account',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.loanAccountNumber}</span>,
    meta: { noWrap: true, widthClass: 'w-36', card: 'lead' },
  }),
  ageingHelper.accessor('customerName', {
    header: 'Customer',
    enableSorting: false,
    cell: (context) => <span className="font-medium text-heading">{context.row.original.customerName}</span>,
    meta: { card: 'title' },
  }),
  ageingHelper.accessor('groupName', {
    header: 'Group',
    enableSorting: false,
    cell: (context) => context.row.original.groupName ?? <span className="text-muted">Individual</span>,
    meta: { widthClass: 'w-40' },
  }),
  ageingHelper.accessor('daysInArrears', {
    header: 'Days in arrears',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric font-semibold tabular-nums text-danger-text">
        {context.row.original.daysInArrears}
      </span>
    ),
    meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Days in arrears' },
  }),
  ageingHelper.accessor('bucketName', {
    header: 'Bucket',
    enableSorting: false,
    cell: (context) => (
      <div className="flex flex-col gap-1">
        <span className="text-xs">{context.row.original.bucketName}</span>
        {context.row.original.classDiffersFromAgeing ? (
          <Badge tone="warning">Class differs</Badge>
        ) : null}
      </div>
    ),
    meta: { widthClass: 'w-36' },
  }),
  ageingHelper.accessor('totalOutstanding', {
    header: 'Outstanding',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.totalOutstanding.minor}
        currency={context.row.original.totalOutstanding.currency}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Outstanding' },
  }),
  ageingHelper.accessor('amountInArrears', {
    header: 'In arrears',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.amountInArrears.minor}
        currency={context.row.original.amountInArrears.currency}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'In arrears' },
  }),
  ageingHelper.accessor('provisionRequired', {
    header: 'Provision',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.provisionRequired.minor}
        currency={context.row.original.provisionRequired.currency}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Provision required' },
  }),
])

function AgeingTable({
  report,
  isLoading,
  isFetching,
  error,
  onRetry,
  page,
  pageSize,
  onPaginationChange,
  branchId,
  loanOfficerId,
  asOf,
  arrearsOnly,
  minimumDaysInArrears,
}: {
  report: ArrearsAgeingReport | undefined
  isLoading: boolean
  isFetching: boolean
  error: ApiError | null
  onRetry: () => void
  page: number
  pageSize: number
  onPaginationChange: OnChangeFn<PaginationState>
  branchId: string | null
  loanOfficerId: string | null
  asOf: string
  arrearsOnly: boolean
  minimumDaysInArrears: number | null
}) {
  return (
    <div className="space-y-3">
      {report ? <ReportHeaderNotice header={report.header} /> : null}
      <ReportExportButtons
        query={{
          report: 'ArrearsAgeing',
          asOf,
          arrearsOnly,
          minimumDaysInArrears,
          branchId,
          loanOfficerId,
          page,
          pageSize,
        }}
      />
      <DataTable
        columns={ageingColumns}
        data={report?.rows}
        rowCount={report?.paging.totalRows}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={onRetry}
        getRowId={(row) => row.loanId}
        emptyTitle={arrearsOnly ? 'Nothing in arrears' : 'No facilities match'}
        emptyDescription={
          arrearsOnly
            ? 'No facility in this scope is behind on this date.'
            : 'No facility matches this branch, officer and date.'
        }
      />
    </div>
  )
}
